import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, FileField, Select, Skeleton, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import type { FinancingTabProps } from '../record/RecordFrame';
import {
  uploadChecklistDocument,
  useChecklist,
  useDocumentTypes,
  useFinancierRealtime,
  useRefreshFinancing,
  type ChecklistGroup,
  type ChecklistItem,
  type ChecklistView,
  type FinancierRecordFields,
} from './api';
import { AdvisorCard, CustomerProfileCard } from './sidebars';
import styles from './financier.module.css';

/**
 * Tab „Unterlagen" (Finanzierer 03/05): warning line, the checklists „Vom Kunden" / „Von Ihnen", the broker's
 * papers; sidebar „Kundenprofil" + „Berater". Uploads go through `POST /documents` with the financing as owner.
 */

/** Accepted by `POST /documents` (magic bytes PDF/JPEG/PNG). */
const DOCUMENT_ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';
/** The contract has its own route after the signature (FZ-07); it is not uploaded as a checklist document. */
const CONTRACT_KEY = 'financing_contract';

function CheckIcon({ done }: { done: boolean }) {
  return done ? (
    <span className={styles.checkDone} aria-hidden="true">
      <span className={styles.checkMark} />
    </span>
  ) : (
    <span className={styles.checkOpen} aria-hidden="true" />
  );
}

function stateLabel(item: ChecklistItem): string {
  return item.state === 'done' ? 'erledigt' : item.state === 'requested' ? 'angefordert' : 'offen';
}

function ChecklistRow({
  item,
  onUpload,
  uploading,
}: {
  item: ChecklistItem;
  onUpload: ((item: ChecklistItem) => void) | null;
  uploading: boolean;
}) {
  const done = item.state === 'done';
  return (
    <li className={styles.checkRow}>
      <CheckIcon done={done} />
      <span className={styles.checkText}>
        <span className={styles.checkName}>
          {item.title}
          <span className={styles.srOnly}> · {stateLabel(item)}</span>
        </span>
        {item.sub ? (
          <span className={clsx(styles.checkSub, !done && styles.checkSubWarn)}>{item.sub}</span>
        ) : null}
      </span>
      {onUpload && !done ? (
        <button
          type="button"
          className={styles.link}
          disabled={uploading}
          aria-label={`${item.title} hochladen`}
          onClick={() => onUpload(item)}
        >
          {uploading ? 'lädt …' : 'hochladen'}
        </button>
      ) : null}
    </li>
  );
}

function ChecklistCard({
  title,
  count,
  group,
  onUpload,
  uploadingKey,
  footer,
}: {
  title: string;
  count: string | null;
  group: ChecklistGroup;
  onUpload: ((item: ChecklistItem) => void) | null;
  uploadingKey: string | null;
  footer?: ReactNode;
}) {
  return (
    <section className={clsx(styles.card, styles.checkCard)} aria-label={title}>
      <div className={styles.checkHead}>
        <span className={styles.checkTitle}>{title}</span>
        <span className={styles.checkCount}>{count}</span>
      </div>
      <ul className={styles.checkList}>
        {group.items.map((item) => (
          <ChecklistRow
            key={item.key}
            item={item}
            onUpload={onUpload}
            uploading={uploadingKey === item.key}
          />
        ))}
      </ul>
      {footer ? <div className={styles.checkFoot}>{footer}</div> : null}
    </section>
  );
}

/** „Datei hochladen" of „Von Ihnen": which of the open items, and the file (no frame beyond the button). */
function FinancierUploadDialog({
  open,
  financing,
  items,
  typeIdOf,
  onClose,
  onDone,
}: {
  open: boolean;
  financing: FinancierRecordFields;
  items: ChecklistItem[];
  typeIdOf: (key: string) => string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const formId = useId();
  const [key, setKey] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKey(items[0]?.key ?? '');
    setFile(null);
    setError(null);
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const typeId = typeIdOf(key);
    if (!file || !typeId) {
      setError(file ? 'Bitte wählen Sie eine Unterlage.' : 'Bitte eine Datei hochladen.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await uploadChecklistDocument({
        financingId: financing.id,
        party: 'financier',
        documentTypeId: typeId,
        documentRequestId: null,
        file,
      });
      onDone();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={`${financing.numberLabel} · ${financing.linked.contact.name}`}
      title="Datei hochladen"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={!file || !key}>
            Hochladen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <Select
          label="Unterlage"
          options={items.map((item) => ({ value: item.key, label: item.title }))}
          value={key}
          onChange={(event) => setKey(event.target.value)}
        />
        <FileField
          label="Datei"
          labelHidden
          title="Datei hierher ziehen"
          description="PDF, JPG oder PNG."
          buttonLabel="Datei wählen"
          accept={DOCUMENT_ACCEPT}
          multiple={false}
          onFiles={(files) => setFile(files[0] ?? null)}
          items={file ? [{ id: 'file', name: file.name }] : []}
          onRemove={() => setFile(null)}
        />
      </form>
    </Dialog>
  );
}

/** „Unterlagen des Vermittlers … liegen vor" with „4 Dateien ansehen" (the deal's documents tab). */
function BrokerNoteCard({ note }: { note: NonNullable<ChecklistView['brokerNote']> }) {
  const navigate = useNavigate();
  return (
    <section className={clsx(styles.card, styles.hintCard)} aria-label="Unterlagen des Vermittlers">
      <div className={styles.hintRow}>
        <span className={styles.hintText}>{note.text}</span>
        <button type="button" className={styles.link} onClick={() => void navigate(note.href)}>
          {`${note.count} ${note.count === 1 ? 'Datei' : 'Dateien'} ansehen`}
        </button>
      </div>
    </section>
  );
}

function DocumentsSkeleton() {
  return (
    <div className={styles.main} aria-busy="true">
      <Skeleton variant="block" height={50} />
      <div className={styles.checklists}>
        <Skeleton variant="block" height={420} />
        <Skeleton variant="block" height={260} />
      </div>
    </div>
  );
}

export function DocumentsTab(props: FinancingTabProps) {
  const record: FinancierRecordFields = props.financing;
  const checklist = useChecklist(record.id);
  const types = useDocumentTypes();
  const refresh = useRefreshFinancing(record.id);
  const { toast } = useToast();
  useFinancierRealtime(record.id);
  const fileRef = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState<ChecklistItem | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [financierUpload, setFinancierUpload] = useState(false);

  const canUpload = record.status === 'active' && record.actions.requestDocuments.allowed;
  const typeIdOf = (key: string) => types.data?.items.find((type) => type.key === key)?.id ?? null;

  async function done() {
    await refresh();
    props.onChanged();
  }

  function pickFor(item: ChecklistItem) {
    setTarget(item);
    fileRef.current?.click();
  }

  async function uploadCustomerFile(file: File) {
    const item = target;
    if (!item) return;
    const typeId = typeIdOf(item.key);
    if (!typeId) {
      toast({ message: 'Die Unterlagenart ist nicht eingerichtet.' });
      return;
    }
    setUploadingKey(item.key);
    try {
      await uploadChecklistDocument({
        financingId: record.id,
        party: 'customer',
        documentTypeId: typeId,
        documentRequestId: item.requestId,
        file,
      });
      toast({ message: `${item.title} hochgeladen.` });
      await done();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setUploadingKey(null);
      setTarget(null);
    }
  }

  const data = checklist.data;
  const openFinancierItems = data
    ? data.financier.items.filter((item) => item.state !== 'done' && item.key !== CONTRACT_KEY)
    : [];

  return (
    <div className={clsx(styles.content, styles.contentTall)}>
      <input
        ref={fileRef}
        type="file"
        className={styles.srOnly}
        accept={DOCUMENT_ACCEPT}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void uploadCustomerFile(file);
        }}
      />
      {checklist.isPending ? <DocumentsSkeleton /> : null}
      {checklist.isError ? (
        <div className={styles.main}>
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void checklist.refetch()}>
                Erneut versuchen
              </Button>
            }
          >
            {errorMessage(checklist.error)}
          </Banner>
        </div>
      ) : null}
      {data ? (
        <div className={styles.main}>
          {data.warning ? (
            <div className={styles.warnLine} role="status">
              <span className={styles.warnTitle}>{data.warning.title}</span>
              {data.warning.text ? <span className={styles.warnText}>{data.warning.text}</span> : null}
            </div>
          ) : null}
          {data.completeCard && !data.warning ? (
            <section className={styles.card} aria-label={data.completeCard.title}>
              <div className={styles.cardTitle}>{data.completeCard.title}</div>
              <div className={styles.completeText}>{data.completeCard.text}</div>
            </section>
          ) : null}
          <div className={styles.checklists}>
            <ChecklistCard
              title="Vom Kunden"
              count={`${data.customer.done} von ${data.customer.total}`}
              group={data.customer}
              onUpload={canUpload ? pickFor : null}
              uploadingKey={uploadingKey}
            />
            <ChecklistCard
              title="Von Ihnen"
              count={null}
              group={data.financier}
              onUpload={null}
              uploadingKey={null}
              footer={
                canUpload && openFinancierItems.length > 0 ? (
                  <Button variant="secondary" size="small" onClick={() => setFinancierUpload(true)}>
                    Datei hochladen
                  </Button>
                ) : null
              }
            />
          </div>
          {data.brokerNote ? <BrokerNoteCard note={data.brokerNote} /> : null}
        </div>
      ) : null}
      <aside className={styles.side}>
        <CustomerProfileCard financing={record} />
        <AdvisorCard financing={record} />
      </aside>
      {data ? (
        <FinancierUploadDialog
          open={financierUpload}
          financing={record}
          items={openFinancierItems}
          typeIdOf={typeIdOf}
          onClose={() => setFinancierUpload(false)}
          onDone={() => {
            setFinancierUpload(false);
            toast({ message: 'Datei hochgeladen.' });
            void done();
          }}
        />
      ) : null}
    </div>
  );
}
